import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Swords, X, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";

function useCountdown(endsAt, active) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!active || !endsAt) return;
    const tick = () => setLeft(Math.max(0, Math.floor((new Date(endsAt) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt, active]);
  return left;
}

function HostChip({ host, win, color, right }) {
  return (
    <div className={`flex items-center gap-1.5 ${right ? "flex-row-reverse" : ""}`}>
      <div className="relative">
        {win && <Crown size={12} className="absolute -top-3 left-1/2 -translate-x-1/2 text-amber-400" />}
        <img src={host?.avatar} alt="" className="w-7 h-7 rounded-full border-2 object-cover" style={{ borderColor: color, boxShadow: `0 0 8px ${color}` }} />
      </div>
      <span className="text-[10px] font-bold text-white max-w-[72px] truncate" style={{ textShadow: `0 0 6px ${color}` }}>{host?.name}</span>
    </div>
  );
}

const winnerHost = (battle) => (battle.hosts || []).find((h) => h.id === battle.winner_id);

export function PKBar({ battle }) {
  const active = battle.status === "active";
  const ended = battle.status === "ended";
  const left = useCountdown(battle.ends_at, active);
  const hosts = (battle.hosts || []).filter((h) => h.accepted !== false);
  const multi = hosts.length > 2;
  const mm = Math.floor(left / 60);
  const ss = String(left % 60).padStart(2, "0");
  const total = (battle.score_a || 0) + (battle.score_b || 0);
  const pctA = total === 0 ? 50 : Math.max(8, Math.min(92, Math.round((battle.score_a / total) * 100)));

  if (multi) {
    // Scores live on the video tiles; the bar is a slim timer + result banner.
    const w = winnerHost(battle);
    return (
      <div className="absolute top-12 left-3 right-3 z-20 pointer-events-none" data-testid="pk-bar">
        <div className="flex justify-center">
          <div className="glass-dark rounded-full px-3 py-1 text-[10px] font-extrabold text-white flex items-center gap-1.5" data-testid="pk-timer">
            <Swords size={11} className="text-pink-400" />
            {hosts.length}-HOST PK · {ended ? "ENDED" : `${mm}:${ss}`}
          </div>
        </div>
        {ended && (
          <div className="mt-2 flex justify-center">
            <div className="gift-pop glass-dark rounded-full px-4 py-1.5 text-xs font-extrabold flex items-center gap-1.5" data-testid="pk-result">
              {battle.winner_id === "tie" || !w ? (
                <span className="text-zinc-200">🤝 It's a TIE!</span>
              ) : (
                <>
                  <Crown size={13} className="text-amber-400" />
                  <span className="gradient-text">{w.name} WINS the PK!</span>
                  <span>😵</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="absolute top-16 left-3 right-3 z-20 pointer-events-none" data-testid="pk-bar">
      <div className="flex justify-center mb-2">
        <div className="glass-dark rounded-full px-3.5 py-1.5 text-[11px] font-extrabold text-white flex items-center gap-1.5">
          <Swords size={11} className="text-[#FF4D8D]" /> PK Battle · Azadi Fest 🇮🇳
        </div>
      </div>
      <div className="flex items-center justify-between mb-1.5">
        <HostChip host={battle.host_a} win={ended && battle.winner === "a"} color="#FF4D8D" />
        <HostChip host={battle.host_b} win={ended && battle.winner === "b"} color="#00E0C7" right />
      </div>
      <div className="relative">
        <div className="h-5 rounded-full overflow-hidden flex border border-white/20 shadow-[0_0_16px_rgba(255,77,141,0.3)]">
          <div className="h-full flex items-center pl-2.5 text-[9px] font-extrabold text-white transition-all duration-700"
            style={{ width: `${pctA}%`, background: "linear-gradient(90deg,#FF4D8D,#FF2E97)" }} data-testid="pk-score-a">
            {(battle.score_a || 0).toLocaleString()}
          </div>
          <div className="h-full flex-1 flex items-center justify-end pr-2.5 text-[9px] font-extrabold text-[#052925] transition-all duration-700"
            style={{ background: "linear-gradient(90deg,#00E0C7,#00B5A3)" }} data-testid="pk-score-b">
            {(battle.score_b || 0).toLocaleString()}
          </div>
        </div>
        <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-white"
          style={{ left: `${pctA}%`, boxShadow: "0 0 8px #fff, 0 0 18px #FFD86B" }} />
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[52px] h-[52px] rounded-full flex flex-col items-center justify-center border-2 border-[#FFD86B] shadow-[0_0_18px_rgba(255,216,107,0.55)]"
          style={{ background: "radial-gradient(circle at 35% 30%, #2A2140, #0B0B14)" }}>
          <span className="font-serif-display text-[#FFD86B] font-bold text-[15px] leading-none">VS</span>
          <span className="text-white text-[9px] font-bold tabular-nums mt-0.5" data-testid="pk-timer">{ended ? "END" : `${mm}:${ss}`}</span>
        </div>
      </div>
      {ended && (
        <div className="mt-4 flex justify-center">
          <div className="gift-pop glass-dark rounded-full px-4 py-1.5 text-xs font-extrabold flex items-center gap-1.5" data-testid="pk-result">
            {battle.winner === "tie" ? (
              <span className="text-zinc-200">🤝 It's a TIE!</span>
            ) : (
              <>
                <Crown size={13} className="text-amber-400" />
                <span className="gradient-text">{(battle.winner === "a" ? battle.host_a : battle.host_b)?.name} WINS the PK!</span>
                <span>😵</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PKChallengeSheet({ open, onClose, myStreamId }) {
  const [streams, setStreams] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setStreams(null);
      setSelected([]);
      api.get("/streams?tab=popular")
        .then(({ data }) => setStreams(data.streams.filter((s) => s.id !== myStreamId)))
        .catch(() => setStreams([]));
    }
  }, [open, myStreamId]);

  const toggle = (id) => {
    setSelected((sel) => sel.includes(id)
      ? sel.filter((x) => x !== id)
      : sel.length >= 3 ? (toast("Max 3 opponents — 4 hosts total ⚔️"), sel) : [...sel, id]);
  };

  const start = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pk/challenge", { target_stream_ids: selected });
      toast.success(data.auto_accepted ? "PK battle started! ⚔️" : "Challenges sent — waiting for hosts to accept…");
      onClose();
    } catch (e) {
      toast.error(errText(e));
    }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 z-[10000]" onClick={onClose} />
          <motion.div data-testid="pk-challenge-sheet"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-[10001] glass-dark rounded-t-3xl bg-[#14141c]/95 max-h-[68%] flex flex-col pb-4">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <p className="font-extrabold font-display text-sm flex items-center gap-1.5"><Swords size={15} className="text-pink-400" /> Challenge hosts to PK</p>
              <button data-testid="pk-sheet-close" onClick={onClose} className="text-zinc-400"><X size={18} /></button>
            </div>
            <p className="text-[10px] text-zinc-500 px-4 mb-2">Pick 1-3 hosts (up to a 4-host battle). The host whose fans send the most gift coins wins! ⚔️</p>
            <div className="overflow-y-auto px-3 space-y-2 flex-1">
              {streams === null && [1, 2, 3].map((i) => <div key={i} className="skeleton h-14 rounded-2xl" />)}
              {streams?.length === 0 && <p className="text-zinc-600 text-xs text-center py-6">No other live hosts right now</p>}
              {streams?.map((s) => {
                const sel = selected.includes(s.id);
                return (
                  <button key={s.id} onClick={() => toggle(s.id)} data-testid={`pk-target-${s.id}`}
                    className={`w-full flex items-center justify-between rounded-2xl px-3 py-2.5 transition-colors ${sel ? "bg-pink-500/15 border border-pink-500/50" : "bg-white/5 border border-transparent"}`}>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <img src={s.host?.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                      <div className="min-w-0 text-left">
                        <p className="text-white text-xs font-bold truncate">{s.host?.name}</p>
                        <p className="text-zinc-500 text-[9px] truncate">👁 {s.viewers?.toLocaleString()} · {s.title}</p>
                      </div>
                    </div>
                    <span data-testid={`pk-select-${s.id}`}
                      className={`w-5 h-5 rounded-full border-2 shrink-0 ml-2 flex items-center justify-center ${sel ? "bg-pink-500 border-pink-500" : "border-zinc-500"}`}>
                      {sel && <span className="text-white text-[10px] font-extrabold">✓</span>}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="px-4 pt-3">
              <button data-testid="pk-start-multi" onClick={start} disabled={!selected.length || busy}
                className="w-full gradient-primary text-white text-sm font-extrabold py-3 rounded-full disabled:opacity-40 active:scale-[0.98] transition-transform">
                {busy ? "Starting…" : selected.length ? `⚔️ Start PK with ${selected.length} host${selected.length > 1 ? "s" : ""}` : "Select hosts to battle"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
